import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Star1, TickCircle, CloseCircle } from 'iconsax-react';
import { fetchFeaturedReviews, type FeaturedReview } from './reviewShared';

// 지어낸 '활동 알림'(도시·문장·몇 분 전·별점을 무작위로 만들던 상자) 자리에 대표가 고른 진짜 후기 — 대표 10/28 20:4x 터미널 "넣어봐 응해"
// (직전 보고 질문: "결제 화면과 첫 화면의 지어낸 '활동 알림'을 끄고, 그 자리에 고르신 진짜 후기를 넣을까요?").
// 자리·크기는 예전 상자 그대로(첫 화면 = 로그인 안 한 손님, 방문자 분석 = 결과 단계). 지어낸 시간·도시·별점은 없다 — 별점·제목·본문·날짜는 후기 원문 그대로.
// 고른 후기를 한 번씩만 차례로 보여 주고 멈춘다(예전처럼 끝없이 돌지 않음). 닫으면 그 화면에서는 다시 안 뜬다. 고른 후기가 없으면 아무것도 안 그린다.

interface Props {
    enabled: boolean;
    /** 첫 상자가 뜨기까지(초) */
    initialDelay?: number;
    /** 한 장이 떠 있는 시간(초) */
    displayDuration?: number;
    /** 다음 장까지 쉬는 시간(초) */
    gap?: number;
    /** 같은 화면에 이미 한 장(맨 앞 후기)이 붙어 있으면 그 다음 후기부터 */
    startIndex?: number;
    /** PC에서 뜨는 자리 — 화면마다 빈 곳이 달라 고른다(첫 화면은 오른쪽 위에 휴대폰 그림이 있어 아래). 휴대폰은 늘 아래 */
    desktop?: 'top' | 'bottom';
}

const fmtDate = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

export const ReviewToast: React.FC<Props> = ({ enabled, initialDelay = 3, displayDuration = 8, gap = 6, startIndex = 0, desktop = 'top' }) => {
    const [reviews, setReviews] = useState<FeaturedReview[]>([]);
    const [current, setCurrent] = useState<FeaturedReview | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const timers = useRef<number[]>([]);

    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        fetchFeaturedReviews(12)
            .then((rows) => { if (alive) setReviews(rows); })
            .catch(() => undefined); // 후기를 못 읽으면 아무것도 안 띄운다
        return () => { alive = false; };
    }, [enabled]);

    useEffect(() => {
        const clear = () => { timers.current.forEach((t) => window.clearTimeout(t)); timers.current = []; };
        if (!enabled || dismissed || reviews.length === 0) { clear(); setCurrent(null); return clear; }
        const order = [...reviews.slice(startIndex), ...reviews.slice(0, startIndex)];
        let at = initialDelay * 1000;
        order.forEach((r) => {
            timers.current.push(window.setTimeout(() => setCurrent(r), at));
            timers.current.push(window.setTimeout(() => setCurrent(null), at + displayDuration * 1000));
            at += (displayDuration + gap) * 1000;
        });
        return clear;
    }, [enabled, dismissed, reviews, startIndex, initialDelay, displayDuration, gap]);

    if (!enabled || dismissed) return null;
    // 휴대폰은 아래에서, PC는 위에서 들어온다(자리와 같은 쪽에서)
    const fromTop = desktop === 'top' && typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches;

    return (
        <AnimatePresence>
            {current && (
                <motion.aside
                    key={current.id}
                    aria-label="먼저 이용하신 분의 후기"
                    initial={{ opacity: 0, y: fromTop ? -40 : 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: fromTop ? -40 : 40 }}
                    transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
                    className={`fixed bottom-[calc(12px+env(safe-area-inset-bottom))] left-3 right-3 sm:left-auto sm:right-6 z-[60] sm:max-w-[300px] sm:w-full ${desktop === 'top' ? 'sm:bottom-auto sm:top-[calc(8.5rem+env(safe-area-inset-top))]' : 'sm:bottom-6'}`}
                >
                    <div className="rounded-[20px] border border-white/[0.10] bg-[#0F0F11]/95 backdrop-blur-md p-3.5 shadow-[0_12px_40px_rgba(0,0,0,0.55)]">
                        <div className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-0.5" aria-label={`별 ${current.rating}개`}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Star1 key={i} size={12} variant={i <= current.rating ? 'Bold' : 'Linear'} color={i <= current.rating ? '#FFB800' : '#3F3F46'} />
                                ))}
                            </span>
                            <button
                                type="button"
                                onClick={() => setDismissed(true)}
                                aria-label="닫기"
                                className="-mr-1 rounded-full p-0.5 text-[#71717A] hover:text-[#FAFAFA]"
                            >
                                <CloseCircle size={18} color="currentColor" />
                            </button>
                        </div>
                        <p className="mt-1.5 text-[13.5px] font-semibold leading-snug text-[#FAFAFA] line-clamp-1 break-keep">{current.title}</p>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-[#A1A1AA] line-clamp-2 break-keep">{current.body}</p>
                        <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-[11.5px] text-[#D4D4D8]">{current.name} 님</span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#34D399]">
                                <TickCircle size={11} variant="Bold" color="#34D399" /> 결제·이용 확인
                            </span>
                            {current.rewarded && <span className="text-[11px] text-[#71717A]">감사 크레딧 받은 후기</span>}
                            <span className="ml-auto text-[11px] tabular-nums text-[#8B8B94]">{fmtDate(current.created_at)}</span>
                        </div>
                    </div>
                </motion.aside>
            )}
        </AnimatePresence>
    );
};

export default ReviewToast;
